import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useConfigStore } from '@dashboard/stores/configStore'
import { questionSchema, type Question } from '@dashboard/types/config'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@dashboard/services/analytics'
import { storeToRefs } from 'pinia'
import { getQuestionDeleteImpact, removeQuestionFormFields } from '../services/questionActions'

export const useQuestionStore = defineStore('question', () => {
  const configStore = useConfigStore()
  const { config, isLoading, isError } = storeToRefs(configStore)

  const questions = computed<Question[]>(() => config.value?.rightAsk?.questions || [])

  const getById = (id: string): Question | undefined => questions.value.find((question: Question) => question.id === id)
  const getBySegmentGroupId = (id: string): Question | undefined =>
    questions.value.find((question: Question) => question.dimensionId === id)

  const destroy = (question: Question) => {
    const foundIndex = config.value?.rightAsk?.questions.findIndex((q) => q.id === question.id)
    if (foundIndex === undefined || foundIndex === -1) {
      return
    } // this should really never happen
    const impact = config.value && getQuestionDeleteImpact(config.value, question.id)
    if (impact && Object.values(impact.blockedBy).some((uses) => uses.length)) {
      throw new Error(impact.message)
    }
    if (config.value) {
      removeQuestionFormFields(config.value, [question.id, ...(question.dimensionId ? [question.dimensionId] : [])])
    }

    void recordAnalyticsEvent(AnalyticsEventTypes.QUESTION_DELETED, {
      questionId: question.id,
      questionText: question.text,
      dimensionId: question.dimensionId
    })

    config.value?.rightAsk?.questions?.splice(foundIndex, 1)
  }

  const createInputQuestion = (questionData: Partial<Question> & Pick<Question, 'id' | 'text'>) => {
    if (!config.value) {
      throw new Error('Load the project before creating a question')
    }
    const newQuestion = questionSchema.parse({
      displayType: 'input',
      inputSettings: {},
      otherOptions: { enabled: false },
      isQuestion: true,
      ...questionData,
      dimensionId: null,
      options: []
    })
    config.value.rightAsk.questions.push(newQuestion)

    return newQuestion
  }

  const updateInputQuestion = (questionData: Partial<Question> & Pick<Question, 'id' | 'text'>) => {
    const question = getById(questionData.id)
    if (!question) {
      return
    }

    const updated = questionSchema.parse({
      ...question,
      ...questionData,
      dimensionId: null,
      options: []
    })
    Object.assign(question, updated)

    return question
  }

  return {
    questions,
    getById,
    getBySegmentGroupId,
    destroy,
    createInputQuestion,
    updateInputQuestion,
    isLoading,
    isError
  }
})
